/**
 * Everything the table components need, derived from one snapshot.
 *
 * Nothing here decides a rule: legality, whose turn it is and what may be
 * called all arrive precomputed in `actions`. This module only answers
 * questions about *presentation* — where a seat sits on a phone held by this
 * viewer, which cards are face up, what the score line reads.
 */
import {
  partnerOf,
  type CardId,
  type GameTypes,
  type LegalPlay,
  type Seat,
  type TableSnapshot,
  type TeamId,
  type Trick,
  type VisibleHandState,
  type VisibleHolding,
  type VisibleMatchState,
  type ViewerActions,
} from '@cards/shared'
import { getGameUi, type GameUi } from '../games/registry'

export type Match = VisibleMatchState<GameTypes>
export type Hand = VisibleHandState<GameTypes>
export type Position = 'n' | 'e' | 's' | 'w'

export interface SeatView {
  readonly seat: Seat
  readonly name: string
  readonly isBot: boolean
  readonly isMe: boolean
  readonly isPartner: boolean
  readonly connected: boolean
  /** A bot is standing in for a disconnected human; the seat is still theirs. */
  readonly awayBot: boolean
  readonly position: Position
  readonly holding: VisibleHolding | null
  readonly playedCard: CardId | null
  readonly toAct: boolean
  readonly active: boolean
  readonly tricksWon: number
}

export interface TableView {
  readonly snapshot: TableSnapshot
  readonly match: Match | null
  readonly hand: Hand | null
  readonly actions: ViewerActions | null
  readonly gameUi: GameUi
  readonly mySeat: Seat | null
  readonly myTeam: TeamId | null
  readonly seatCount: number
  readonly seats: readonly SeatView[]
  readonly at: (position: Position) => SeatView | null
  readonly trick: Trick | null
  readonly previousTrick: Trick | null
  readonly scores: { readonly us: number; readonly them: number }
  readonly myCards: readonly CardId[]
  readonly legalPlays: ReadonlyMap<CardId, LegalPlay>
  readonly toAct: Seat | null
  readonly isMyTurn: boolean
  readonly tricksPerHand: number
  /** One dot per trick of the hand, grouped rather than in play order: the
   *  contract side's tricks fill from the left towards the goal ring, the
   *  defenders' from the right, and the unplayed ones sit hollow between. */
  readonly trickDots: readonly ('us' | 'them' | null)[]
  /** Where on the track a trick's dot landed, by trick index; null if unplayed. */
  readonly trickDotSlot: (trickIndex: number) => number | null
  readonly name: (seat: Seat | null) => string
}

/** The contract type is module-defined; every game names its seat with one of
 *  these fields, as the core's own `contractorSeatOf` reads it. */
export function contractorSeatOf(contract: unknown, seatCount: number): Seat | null {
  if (typeof contract !== 'object' || contract === null) return null
  const bag = contract as Record<string, unknown>
  for (const key of ['contractor', 'declarer', 'taker', 'pitcher', 'seat']) {
    const v = bag[key]
    if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < seatCount) return v
  }
  return null
}

/** Seat index is turn order, so the layout is a rotation of it. Clockwise play
 *  passes to the player on your left, which is the west position on screen. */
function positionOf(seat: Seat, viewSeat: Seat, count: number, clockwise: boolean): Position {
  const step = (seat - viewSeat + count) % count
  if (count !== 4) return step === 0 ? 's' : 'n'
  if (step === 0) return 's'
  if (step === 2) return 'n'
  return clockwise ? (step === 1 ? 'w' : 'e') : step === 1 ? 'e' : 'w'
}

export function buildView(snapshot: TableSnapshot): TableView {
  const match = snapshot.match
  const hand = match?.hand ?? null
  const actions = snapshot.actions
  const gameUi = getGameUi(snapshot.room.gameId)
  const mySeat = snapshot.you.seat
  const table = match?.table ?? null
  const seatCount = table?.seatCount ?? snapshot.room.seats.length
  // A spectator still needs a bottom seat to orient the table from.
  const viewSeat = mySeat ?? 0
  const myTeam = table && mySeat !== null ? (table.teamOf[mySeat] ?? null) : null
  const partner = table && mySeat !== null ? partnerOf(table, mySeat) : null
  const trick = hand && hand.tricks.length > 0 ? hand.tricks[hand.tricks.length - 1] : null
  const previousTrick = hand && hand.tricks.length > 1 ? hand.tricks[hand.tricks.length - 2] : null

  const name = (seat: Seat | null): string => {
    if (seat === null) return ''
    const player = match?.players[seat]
    if (player) return player.name
    const occupant = snapshot.room.seats[seat]
    if (occupant && occupant.kind !== 'empty') return occupant.name
    return `Seat ${seat + 1}`
  }

  const tricksWon: number[] = new Array(seatCount).fill(0)
  for (const t of hand?.tricks ?? []) {
    if (t.winner !== null) tricksWon[t.winner] = (tricksWon[t.winner] ?? 0) + 1
  }

  const seats: SeatView[] = []
  for (let seat = 0; seat < seatCount; seat++) {
    const occupant = snapshot.room.seats[seat]
    const player = match?.players[seat]
    const isBot = player?.isBot ?? occupant?.kind === 'bot'
    seats.push({
      seat,
      name: name(seat),
      isBot,
      isMe: seat === mySeat,
      isPartner: partner !== null && seat === partner,
      connected: occupant?.kind === 'human' ? occupant.connected : true,
      awayBot: occupant?.kind === 'human' ? occupant.awayBot : false,
      position: positionOf(seat, viewSeat, seatCount, gameUi.rotation === 'clockwise'),
      holding: hand?.holdings[seat] ?? null,
      playedCard: trick?.plays.find((p) => p.seat === seat)?.card ?? null,
      toAct: actions?.turn === seat,
      active: hand ? hand.activeSeats.includes(seat) : true,
      tricksWon: tricksWon[seat] ?? 0,
    })
  }

  const myHolding = mySeat !== null ? hand?.holdings[mySeat] : undefined
  const myCards = myHolding && myHolding.kind === 'cards' ? myHolding.cards : []

  const legalPlays = new Map<CardId, LegalPlay>()
  for (const p of actions?.legalPlays ?? []) legalPlays.set(p.card, p)

  const scores = (() => {
    const all = match?.scores ?? []
    if (myTeam === null) return { us: all[0] ?? 0, them: all[1] ?? 0 }
    const them = all.findIndex((_, i) => i !== myTeam)
    return { us: all[myTeam] ?? 0, them: all[them] ?? 0 }
  })()

  const tricksPerHand = gameUi.tricksPerHand(match)
  const sideOf = (seat: Seat): 'us' | 'them' =>
    table && myTeam !== null
      ? table.teamOf[seat] === myTeam ? 'us' : 'them'
      : table && table.teamOf[seat] === 0 ? 'us' : 'them'
  // The side that won the auction fills from the left; with no contract yet
  // (or none that names a seat) the viewer's side does.
  const contractor = table ? contractorSeatOf(hand?.contract, table.seatCount) : null
  const leftSide = contractor !== null ? sideOf(contractor) : 'us'
  const trickDots: ('us' | 'them' | null)[] = new Array(tricksPerHand).fill(null)
  const slots: (number | null)[] = []
  let left = 0
  let right = tricksPerHand - 1
  for (let i = 0; i < (hand?.tricks.length ?? 0); i++) {
    const t = hand!.tricks[i]
    if (t.winner === null || left > right) {
      slots.push(null)
      continue
    }
    const side = sideOf(t.winner)
    const slot = side === leftSide ? left++ : right--
    trickDots[slot] = side
    slots.push(slot)
  }

  return {
    snapshot,
    match,
    hand,
    actions,
    gameUi,
    mySeat,
    myTeam,
    seatCount,
    seats,
    at: (position) => seats.find((s) => s.position === position) ?? null,
    trick,
    previousTrick,
    scores,
    myCards,
    legalPlays,
    // Whose turn at the table — public, the viewer's or anybody's. Whether it
    // is the viewer's to act on is `isMyTurn`, from the private `toAct`.
    toAct: actions?.turn ?? null,
    isMyTurn: actions?.toAct !== null && actions?.toAct !== undefined && actions.controls.includes(actions.toAct),
    tricksPerHand,
    trickDots,
    trickDotSlot: (i) => slots[i] ?? null,
    name,
  }
}
